import React, { useState, useEffect } from 'react';

const AnimeForm = ({ onSubmit, initialData }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [banner, setBanner] = useState('');
  const [poster, setPoster] = useState('');
  const [selectedGenres, setSelectedGenres] = useState([]);
  const [releaseYear, setReleaseYear] = useState(new Date().getFullYear());

  useEffect(() => {
    if (initialData) {
      setName(initialData.name || initialData.title || '');
      setDescription(initialData.description || initialData.synopsis || '');
      setBanner(initialData.banner || '');
      setPoster(initialData.poster || initialData.image || '');
      
      // Manejar género como array o string
      if (initialData.genre) {
        if (typeof initialData.genre === 'string') {
          // Si es string, dividirlo por comas y eliminar espacios
          setSelectedGenres(initialData.genre.split(',').map(g => g.trim()));
        } else if (Array.isArray(initialData.genre)) {
          setSelectedGenres(initialData.genre);
        }
      } else {
        setSelectedGenres([]);
      }
      
      setReleaseYear(initialData.releaseYear || initialData.year || new Date().getFullYear());
    }
  }, [initialData]);

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validar que al menos un género esté seleccionado
    if (selectedGenres.length === 0) {
      alert('Por favor, seleccione al menos un género');
      return;
    }
    
    const formData = {
      name,
      description,
      banner,
      poster,
      // Convertir array de géneros a string para compatibilidad con el backend
      genre: selectedGenres.length > 0 ? selectedGenres.join(', ') : '',
      releaseYear: parseInt(releaseYear, 10)
    };
    
    onSubmit(formData);
  };
  
  const handleGenreChange = (genre) => {
    setSelectedGenres(prev => {
      if (prev.includes(genre)) {
        return prev.filter(g => g !== genre);
      } else {
        return [...prev, genre];
      }
    });
  };

  const genreOptions = [
    'Acción',
    'Aventura',
    'Comedia',
    'Drama',
    'Fantasía',
    'Ciencia Ficción',
    'Terror',
    'Romance',
    'Misterio',
    'Sobrenatural',
    'Deportes',
    'Música',
    'Slice of Life',
    'Isekai',
    'Josei',
    'Seinen',
    'Shonen',
    'Thriller',
    'Películas de Anime'
  ];

  return (
    <form onSubmit={handleSubmit} className="space-y-6 bg-gray-900 p-6 rounded-md">
      <div>
        <h3 className="text-lg font-medium mb-4 text-white">Información del Anime</h3>
      </div>

      <div>
        <label htmlFor="name" className="block text-sm font-medium text-gray-300">
          Nombre
        </label>
        <input
          type="text"
          id="name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          className="mt-1 block w-full border border-gray-600 rounded-md shadow-sm p-2 bg-gray-800 text-gray-300"
        />
      </div>

      <div>
        <label htmlFor="description" className="block text-sm font-medium text-gray-300">
          Descripción
        </label>
        <textarea
          id="description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={4}
          className="mt-1 block w-full border border-gray-600 rounded-md shadow-sm p-2 bg-gray-800 text-gray-300"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label htmlFor="banner" className="block text-sm font-medium text-gray-300">
            URL del Banner
          </label>
          <input
            type="text"
            id="banner"
            value={banner}
            onChange={(e) => setBanner(e.target.value)}
            placeholder="https://ejemplo.com/banner.jpg"
            className="mt-1 block w-full border border-gray-600 rounded-md shadow-sm p-2 bg-gray-800 text-gray-300"
          />
        </div>

        <div>
          <label htmlFor="poster" className="block text-sm font-medium text-gray-300">
            URL del Póster
          </label>
          <input
            type="text"
            id="poster"
            value={poster}
            onChange={(e) => setPoster(e.target.value)}
            placeholder="https://ejemplo.com/poster.jpg"
            className="mt-1 block w-full border border-gray-600 rounded-md shadow-sm p-2 bg-gray-800 text-gray-300"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Géneros
          </label>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-60 overflow-y-auto p-2 border border-gray-600 rounded-md bg-gray-800">
            {genreOptions.map((option) => (
              <div key={option} className="flex items-center">
                <input
                  type="checkbox"
                  id={`genre-${option}`}
                  checked={selectedGenres.includes(option)}
                  onChange={() => handleGenreChange(option)}
                  className="mr-2 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-600 rounded bg-gray-700"
                />
                <label htmlFor={`genre-${option}`} className="text-sm text-gray-300">
                  {option}
                </label>
              </div>
            ))}
          </div>
          <p className="mt-2 text-sm text-gray-400">
            Géneros seleccionados: {selectedGenres.length > 0 ? selectedGenres.join(', ') : 'Ninguno'}
          </p>
        </div>

        <div>
          <label htmlFor="releaseYear" className="block text-sm font-medium text-gray-300">
            Año de Lanzamiento
          </label>
          <input
            type="number"
            id="releaseYear"
            value={releaseYear}
            onChange={(e) => setReleaseYear(e.target.value)}
            min={1900}
            max={new Date().getFullYear() + 5}
            required
            className="mt-1 block w-full border border-gray-600 rounded-md shadow-sm p-2 bg-gray-800 text-gray-300"
          />
        </div>
      </div>

      <div className="pt-5">
        <button
          type="submit"
          className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-md"
        >
          Continuar
        </button>
      </div>
    </form>
  );
};

export default AnimeForm;
